import React from "react";

/**
 * 乗番の繋がりを何回・いつ見たか。
 *
 * **回数だけでは意味が伝わらない。**「1回」と書いてあっても、それが
 * 昨日なのか先月なのかで受け取り方が変わる。日替わりで分岐している乗番は
 * それぞれ別の日に観測されているので、日付が出て初めて腑に落ちる。
 */
export function linkTiming(link) {
  if (!link.last_seen_on) return `${link.observed_count}回`;

  const md = link.last_seen_on.slice(5).replace("-", "/");
  return link.observed_count === 1 ? `${md}に1回` : `${link.observed_count}回（最終${md}）`;
}

/**
 * 札に入れる短い形。「3回（最終 8/28）」→「3回・8/28」。
 *
 * **狭い画面でも隠さないために縮める。** 以前は 480px 以下でこの行ごと
 * 消していたが、そうすると ア12 の続きのように
 *
 *   ア79 … 8/25に1回      ← 1 回きり
 *   ア80 … 3回（最終 8/28）← 3 回
 *
 * が同じ顔の札になり、**確からしさの差が見えなくなる。**この図はもともと
 * 札を見比べるためのものなので、比べる材料が消えるのはいちばん困る。
 *
 * 日付が「最後に繋がった日」であることは、この短さでは表しきれない。
 * 凡例と title で補う。
 */
export function linkTimingShort(link) {
  if (!link.last_seen_on) return `${link.observed_count}回`;

  return `${link.observed_count}回・${link.last_seen_on.slice(5).replace("-", "/")}`;
}

/**
 * 流れの中の1つ。押せるものはボタン、いま見ている乗番は押せないので div。
 *
 * 時刻は2行に割る。「06:26〜10:12」を1行に置くと札が広がって、
 * 狭い画面で3列に収まらなくなる。縦に積めば札は車号1つぶんの幅で済む。
 */
function ChainNode({ code, first, last, tripCount, meta, metaFull, partial, self, onClick }) {
  const Tag = self ? "div" : "button";

  return (
    <Tag
      className={`anl-chain__node${self ? " anl-chain__node--self" : ""}`}
      {...(self ? {} : { type: "button", onClick })}
      /* 札には短い形を出し、言葉を補った形はここに置く */
      title={
        meta
          ? `${code} · ${first}〜${last} · ${tripCount}便 · ${metaFull || meta}`
          : undefined
      }
    >
      <span className="anl-chain__code">{code}</span>
      <span className="anl-chain__span">
        {first}
        <span className="anl-chain__to">{last}</span>
      </span>
      <span className="anl-chain__meta">{tripCount}便</span>
      {partial && <span className="anl-chain__partial">{partial}</span>}
      {meta && <span className="anl-chain__meta anl-chain__meta--when">{meta}</span>}
    </Tag>
  );
}

/**
 * 札に出す時刻。
 *
 * **乗り移りは乗番の途中で起きうる。** 乗番の端をそのまま出すと嘘になる。
 * 実例: ア12（終 13:08）から ア50 へ移った日、車は ア50 の 5便目 14:23 から
 * 合流している。ア50 の始発 07:18 を出すと、13:08 のあとに 07:18 が来る。
 *
 * link_departure が乗番の端と違うときは、そちらを起点／終点に差し替え、
 * 「N便目から」と添えて途中から入ったことを示す。
 */
function nodeTimes(d, direction) {
  const joined = d.link_departure;

  if (direction === "next") {
    const partial = joined && joined !== d.first_departure ? `${d.link_seq}便目から` : null;
    return { first: partial ? joined : d.first_departure, last: d.last_departure, partial };
  }

  const partial = joined && joined !== d.last_departure ? `${d.link_seq}便目まで` : null;
  return { first: d.first_departure, last: partial ? joined : d.last_departure, partial };
}

/**
 * 直前 → この乗番 → 直後 を、時間の流れの向きに並べる。
 *
 * 文字を並べただけだと、どれが前でどれが後か、いま見ている乗番がどこに
 * 挟まっているのかが読み取れなかった。**各段に時間帯を添えるのが要点で、**
 * 前の乗番の終発と、この乗番の始発が接していることが目で見える。
 * 「繋がっている」と書くより、時刻が隣り合っているほうが早い。
 *
 * **狭い画面でも横に並べたまま。** 縦積みにすると流れの向きが消えて、
 * ただの箇条書きになる。左から右へ時間が進むことがこの図の中身なので、
 * 折り返すくらいなら字を詰める。
 *
 * previous を渡さなければ「この乗番 → 直後」の2段になる。車号ページの
 * 「乗番●のあと」がその形。
 */
export default function DutyChain({ self, previous, next, selfLabel = "この乗番", onOpen }) {
  const column = (head, duties, direction) => (
    <div className="anl-chain__col">
      <div className="anl-chain__head">{head}</div>
      {duties.length === 0 ? (
        <div className="anl-chain__none">なし</div>
      ) : (
        duties.map((d, i) => {
          const { first, last, partial } = nodeTimes(d, direction);

          return (
            <React.Fragment key={d.id}>
              {i > 0 && <div className="anl-chain__or">または</div>}
              <ChainNode
                code={d.code}
                first={first}
                last={last}
                tripCount={d.trip_count}
                partial={partial}
                meta={linkTimingShort(d)}
                metaFull={linkTiming(d)}
                onClick={() => onOpen(d.id)}
              />
            </React.Fragment>
          );
        })
      )}
    </div>
  );

  return (
    <div className={`anl-chain${previous ? "" : " anl-chain--half"}`}>
      {previous && (
        <>
          {column("直前", previous, "previous")}
          <div className="anl-chain__arrow" aria-hidden="true" />
        </>
      )}
      <div className="anl-chain__col">
        <div className="anl-chain__head">{selfLabel}</div>
        <ChainNode
          self
          code={self.code}
          first={self.first_departure}
          last={self.last_departure}
          tripCount={self.trip_count}
        />
      </div>
      <div className="anl-chain__arrow" aria-hidden="true" />
      {column("直後", next, "next")}
    </div>
  );
}
